import {
  INACTIVE_SERVICE_REQUEST_STATUSES,
  processCalendarEvents,
  processServiceRequestsForCalendar,
} from '@/components/Kanban/ServiceRequests/Calendar/utils';
import AppointmentItem from '@/components/ProjectDetails/Cards/Items/AppointmentItem';
import TaskItem from '@/components/ProjectDetails/Cards/Items/TaskItem';
import {F} from '@/utils/intl';
import {CalendarOutlined, ClockCircleOutlined, PlusOutlined} from '@ant-design/icons';
import {Button, Card, Empty, Space} from 'antd';
import classNames from 'classnames';
import React, {useMemo} from 'react';
import styles from './NextActionCard.less';
import {CheckAccess} from '@/utils/access';
import FEATURE_FLAGS from '@/constants/featureFlags';

export interface NextActionCardProps {
  leadData: any;
  onRefresh?: () => void;
  onAddTask?: () => void;
  onAddStandbyTask?: () => void;
  onAddAppointment?: () => void;
}

const NextActionCard: React.FC<NextActionCardProps> = ({
                                                         leadData,
                                                         onRefresh,
                                                         onAddTask,
                                                         onAddStandbyTask,
                                                         onAddAppointment,
                                                       }) => {
  const nextAction = useMemo(() => {
    if (!leadData) return null;

    const now = new Date();

    const tasks = leadData.tasks || [];
    const serviceRequests = processServiceRequestsForCalendar(
      leadData.service_requests || [],
    );
    const calendarEvents = processCalendarEvents(leadData.calendarEvents || []);
    const allAppointments = [...serviceRequests, ...calendarEvents];

    const taskCandidates = tasks
      // Note: intentionally NOT filtering by can_edit — a task you can't edit is
      // still a valid next action (and the tasks list shows it too). Overdue tasks
      // are kept on purpose: an overdue task is a legitimate next action.
      .filter((t: any) => !t.is_completed && !t.retired_at && t.due_date)
      .map((t: any) => ({
        type: 'task' as const,
        data: t,
        time: new Date(t.due_date),
      }))
      // Guard against unparseable due dates whose NaN sort key would corrupt the winner.
      .filter((candidate: any) => !Number.isNaN(candidate.time.getTime()));

    const appointmentCandidates = allAppointments
      .filter((appointment: any) => {
        if (INACTIVE_SERVICE_REQUEST_STATUSES.includes(appointment.status))
          return false;

        const time = new Date(appointment.utc_start_time);
        return !Number.isNaN(time.getTime()) && time > now;
      })
      .map((appointment: any) => ({
        type: 'appointment' as const,
        data: appointment,
        time: new Date(appointment.utc_start_time),
      }));

    const allCandidates = [...taskCandidates, ...appointmentCandidates].sort(
      (a: any, b: any) => a.time.getTime() - b.time.getTime(),
    );

    if (allCandidates.length === 0) return null;

    return {
      type: allCandidates[0].type,
      data: allCandidates[0].data,
    };
  }, [leadData]);

  if (!nextAction) {
    return (
      <Card
        className={classNames(
          styles.nextActionCard,
          styles.emptyNextActionCard,
        )}
      >
        <div className={styles.emptyStateContainer}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Space direction="vertical" size={4} className={styles.fullWidth}>
                <div>
                  <div className={styles.emptyTitle}>
                    <F
                      id={'pages.lead.no_next_action_defined'}
                      defaultMessage={'No next action defined'}
                    />
                  </div>
                  <div className={styles.emptySubtitle}>
                    <F
                      id={'pages.lead.next_action_define'}
                      defaultMessage={'Define next action'}
                    />
                  </div>
                </div>
                <Space size={8}>
                  <CheckAccess feature={[FEATURE_FLAGS.LEAD_MANAGEMENT]}>
                    <Button
                      type="default"
                      icon={<PlusOutlined />}
                      onClick={onAddTask}
                    >
                      <F
                        id={'pages.lead.next_action_task'}
                        defaultMessage={'Followup Task'}
                      />
                    </Button>
                  </CheckAccess>
                  <CheckAccess feature={[FEATURE_FLAGS.LEAD_MANAGEMENT]}>
                    <Button
                      type="default"
                      icon={<ClockCircleOutlined />}
                      onClick={onAddStandbyTask}
                    >
                      <F
                        id={'pages.lead.next_action_standby_task'}
                        defaultMessage={'Wiedervorlage'}
                      />
                    </Button>
                  </CheckAccess>
                  <Button
                    type="default"
                    icon={<CalendarOutlined />}
                    onClick={onAddAppointment}
                  >
                    <F
                      id={'pages.lead.next_action_book'}
                      defaultMessage={'Book Appointment'}
                    />
                  </Button>
                </Space>
              </Space>
            }
          />
        </div>
      </Card>
    );
  }

  return (
    <Card
      className={classNames(styles.nextActionCard, {
        [styles.nextActionTask]: nextAction?.type === 'task',
        [styles.nextActionAppointment]: nextAction?.type === 'appointment',
      })}
    >
      <div className={styles.nextActionContent}>
        {nextAction.type === 'task' ? (
          <TaskItem nextAction task={nextAction.data} refresh={onRefresh} />
        ) : (
          <AppointmentItem
            nextAction
            appointment={nextAction.data}
            lead={leadData}
            refresh={onRefresh}
          />
        )}
      </div>
    </Card>
  );
};

export default NextActionCard;
